import React, {useEffect, useState} from 'react'
import {Moment} from 'moment'
import {Select} from 'antd'
import {observer} from 'mobx-react'
import {useTranslation} from 'react-i18next'
import {useInjection} from 'dna-react-ioc'
import {PaymentsType} from '~/code/constants'
import {RangePickerContainer, SelectWithTitleContainer, Icon} from '~/code/components'
import {IPaymentsTransactionsFilterStore} from './IPaymentsTransactionsFilterStore'
import {
    OverviewPaymentsCurrencySelectStoreSymbol,
    PaymentsOverviewRangePickerStoreSymbol
} from '../../constants-beta'
import {isDateDisabled} from '../utils'

import styles from './styles.scss'

type NullableRange = [Moment | null, Moment | null] | null

export const PaymentsTransactionsFilterStoreSymbol = Symbol('PaymentsTransactionsFilterStore')

export const OverviewPaymentsFilter = observer(() => {
    const {t} = useTranslation()

    const paymentsFilterStore = useInjection<IPaymentsTransactionsFilterStore>(
        PaymentsTransactionsFilterStoreSymbol
    )

    const {
        paymentsTypes,
        setSelectedTransactionStore,
        paymentsType,
        setPaymentsType,
        transactionStore,
        storesOptions,
        startDate,
        endDate
    } = paymentsFilterStore

    const [rangeTypeSelected, setRangeTypeSelected] = useState<'start' | 'end' | null>(null)
    const [localSelectedDates, setLocalSelectedDates] = useState<NullableRange>(() => [
        startDate,
        endDate
    ])
    const [tempSelectedDates, setTampSelectedDates] = useState<NullableRange>(null)

    const [isCalendarChanged, setCalendarChanged] = useState(false)
    const [isDateClicked, setDateClicked] = useState(false)

    useEffect(() => {
        setLocalSelectedDates([startDate, endDate])
    }, [startDate, endDate])

    const handleCalendarChange = (
        dates: [Moment, Moment],
        range: 'start' | 'end'
    ): [Moment, Moment] => {
        setRangeTypeSelected(range)
        setTampSelectedDates([dates?.[0] ?? null, dates?.[1] ?? null])
        setCalendarChanged(true)

        return dates
    }

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setRangeTypeSelected(null)
            setLocalSelectedDates(undefined)
            setCalendarChanged(false)
            setDateClicked(false)
        }
    }

    const handleStoreChange = (value, option) => {
        setSelectedTransactionStore(option)
    }

    const mappedStoresOptions = React.useMemo(
        () =>
            storesOptions.map((group) => ({
                label: group.label,
                options: group.options.map((opt) => ({
                    value: opt.value,
                    label:
                        opt.type === 'pos' ? (
                            <div className={styles.storeOption}>
                                <span className={styles.storeOptionLabel}>{opt.label}</span>
                                <span className={styles.storeOptionSecondaryLabel}>
                                    {opt.secondaryLabel}
                                </span>
                            </div>
                        ) : (
                            opt.label
                        ),
                    selectLabel: opt.label,
                    type: opt.type
                }))
            })),
        [storesOptions]
    )

    const {value: transactionStoreValue} = transactionStore

    return (
        <>
            <Select
                className={styles.styledSelect}
                style={{width: 165}}
                defaultValue={PaymentsType.all}
                value={paymentsType}
                onChange={setPaymentsType}
                options={paymentsTypes}
                suffixIcon={<Icon type={'caret_down'} />}
            />

            <Select
                className={styles.styledSelect}
                style={{width: 140}}
                optionFilterProp={'selectLabel'}
                optionLabelProp={'selectLabel'}
                popupClassName={styles.storesDropdown}
                popupMatchSelectWidth={false}
                styles={{popup: {root: {width: 280}}}}
                value={transactionStoreValue}
                options={mappedStoresOptions}
                onChange={handleStoreChange}
                suffixIcon={<Icon type={'caret_down'} />}
            />

            <SelectWithTitleContainer
                injectableIdentifier={OverviewPaymentsCurrencySelectStoreSymbol}
                displayValue={t('Overview.currency')}
            />

            <RangePickerContainer
                key={'payments'}
                className={styles.picker}
                value={localSelectedDates}
                cellRender={(current, info) => {
                    const originNode = info.originNode

                    return React.cloneElement(originNode, {
                        onClick: (e: React.MouseEvent) => {
                            originNode.props?.onClick?.(e)
                            setDateClicked(true)
                        }
                    })
                }}
                disabledDate={(currentDate, selectedDates) => {
                    return isDateDisabled(
                        currentDate,
                        tempSelectedDates || selectedDates,
                        rangeTypeSelected,
                        isCalendarChanged,
                        isDateClicked
                    )
                }}
                onOpenChange={(open) => {
                    handleOpenChange(open)
                }}
                onCalendarChange={(dates, _, info) => {
                    handleCalendarChange(dates, info.range)
                }}
                injectableIdentifier={PaymentsOverviewRangePickerStoreSymbol}
            />
        </>
    )
})
